import { Cog, Pencil } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * SystemChip — framework-managed pipeline block.
 *
 * Used between admin stages on the unified pipeline canvas (e.g.
 * `identity_lookup`, `issue_session`). Renders as a slightly desaturated
 * card with a monospace title, a `system` tag, optional Edit affordance,
 * and an optional branch marker. Read-only by default; the `onEdit`
 * callback opens the system block's narrow config surface.
 *
 * Source of truth: `mockups/unified-pipeline.html` `.sys-chip`.
 */
export interface SystemChipProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Block name, shown in monospace (e.g. `identity_lookup`). */
  title: string
  /** Sub-text describing the system block's pinned configuration. */
  description: React.ReactNode
  /** Icon shown in the chip header. Defaults to a generic gear glyph. */
  icon?: React.ReactNode
  /** Tag pill label on the right of the header. Defaults to "system". */
  tag?: string
  /** When present, renders an inline `Edit` affordance in the footer. */
  onEdit?: () => void
  /** When true, renders the rotated-diamond "forks" marker in the footer. */
  showBranchMarker?: boolean
  /**
   * Flow Policy provenance tag (e.g. `"recipe/anomaly-step-up"`). When
   * present, the chip renders with a green left stripe + a red RECIPE
   * badge to signal the block was added or touched by a Flow Policy.
   * Uniform with the branched renderer's InlineNodeTile treatment so
   * linear flows (password-reset / logout / custom_nested) surface
   * recipe-owned blocks the same way as auth-login flows.
   */
  originTag?: string
}

const SYS_CHIP_BASE: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(180deg, color-mix(in srgb, var(--pe-border-card) 18%, transparent) 0%, var(--pe-bg-sys) 100%)',
  borderColor: 'var(--pe-border-sys)',
  borderWidth: 1,
  borderStyle: 'solid',
}

export const SystemChip = React.forwardRef<HTMLDivElement, SystemChipProps>(
  function SystemChip(
    {
      className,
      title,
      description,
      icon,
      tag = 'system',
      onEdit,
      showBranchMarker,
      originTag,
      style,
      ...rest
    },
    ref,
  ) {
    const showFooter = Boolean(onEdit) || Boolean(showBranchMarker)
    const policyStyle: React.CSSProperties = originTag
      ? {
          borderLeftWidth: 3,
          borderLeftColor: 'var(--pe-accent-recipe, #10b981)',
        }
      : {}
    return (
      <div
        ref={ref}
        className={cn(
          'w-full rounded-[10px] p-3.5 transition-colors',
          'hover:[border-color:var(--pe-border-card)]',
          className,
        )}
        style={{ ...SYS_CHIP_BASE, ...policyStyle, ...style }}
        data-testid="pipeline-system-chip"
        data-policy-origin={originTag}
        {...rest}
      >
        <div className="flex items-center gap-2.5 mb-1.5">
          <div
            className="w-[26px] h-[26px] shrink-0 flex items-center justify-center rounded-[5px]"
            style={{
              background: 'color-mix(in srgb, var(--pe-text-primary) 4%, transparent)',
              color: 'var(--pe-text-tertiary)',
            }}
            aria-hidden
          >
            {icon ?? <Cog size={13} strokeWidth={2} />}
          </div>
          <span
            className="flex-1 min-w-0 truncate font-mono text-[12px]"
            style={{ color: 'var(--pe-text-secondary)' }}
          >
            {title}
          </span>
          {originTag && (
            <span
              style={{
                color: '#dc2626',
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                flexShrink: 0,
              }}
              data-testid="recipe-syschip-badge"
              title={`Applied by Flow Policy: ${originTag.replace(/^recipe\//, '')}`}
            >
              recipe
            </span>
          )}
          <span
            className="shrink-0 font-mono text-[8.5px] tracking-[0.10em] uppercase rounded-[3px] px-1.5 py-[2px] border"
            style={{
              color: 'var(--pe-text-quiet)',
              background: 'color-mix(in srgb, var(--pe-text-primary) 2.5%, transparent)',
              borderColor: 'var(--pe-border-sys)',
            }}
          >
            {tag}
          </span>
        </div>

        <div
          className="font-mono text-[10px] leading-snug pl-[36px]"
          style={{ color: 'var(--pe-text-muted)' }}
        >
          {description}
        </div>

        {showFooter && (
          <div
            className="mt-2 pt-2 flex items-center gap-2.5 border-t border-dashed"
            style={{ borderColor: 'var(--pe-border-sys)' }}
          >
            {onEdit && (
              <button
                type="button"
                onClick={onEdit}
                className="inline-flex items-center gap-1 font-mono text-[10px] cursor-pointer transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-[2px]"
                style={{ color: 'var(--pe-accent-preauth)' }}
              >
                <Pencil size={10} strokeWidth={2} />
                Edit
              </button>
            )}
            {showBranchMarker && (
              <span
                className="ml-auto inline-flex items-center gap-1.5 font-mono text-[9.5px] tracking-[0.06em] uppercase"
                style={{ color: 'var(--pe-accent-identify)' }}
                data-testid="pipeline-system-chip-branch"
              >
                <span
                  className="w-[9px] h-[9px] rounded-[1.5px] rotate-45"
                  style={{
                    background: 'var(--pe-accent-identify)',
                    boxShadow: '0 0 6px color-mix(in srgb, var(--pe-accent-identify) 50%, transparent)',
                  }}
                  aria-hidden
                />
                forks
              </span>
            )}
          </div>
        )}
      </div>
    )
  },
)
